import { useCallback, useEffect, useRef, useState } from "react";

import { REDUCED_MOTION } from "../lib/config.js";

/* 랜덤 룰렛 — 한 곳을 툭 던져주면 "왜 하필 거기"가 남는다. 후보들을 훑고
   지나가는 걸 보여주면 뽑는 과정이 눈에 보이고, 점심 고르기가 잠깐 놀이가 된다.

   애니메이션 클래스(roll/win)는 DOM 을 직접 건드리지 않고 상태로 내린다.
   React 가 className 을 다시 쓸 때 손으로 붙인 클래스가 지워지기 때문이다.

   후보는 배열이 아니라 함수로 받는다. '화면 안에서'를 켜면 후보가 지도 범위에 딸려
   다니는데, 그 범위를 상태로 들고 있으면 지도를 조금 미는 것만으로 마커 수백 개가
   다시 그려진다. 룰렛은 버튼을 누른 순간의 답만 있으면 된다. */

const SPIN_STEPS = 16;
const WIN_MS = 2000;      // 마커 파동 2회
const REEL_MS = 2600;     // 릴이 스스로 사라지기까지

export function useRoulette({ candidates, select, toast, emptyMsg = "고를 곳이 없습니다" }) {
  const [spinning, setSpinning] = useState(false);
  const [reel, setReel] = useState(null);        // { tag, name, n, win }
  const [rollingId, setRollingId] = useState(null);
  const [winnerId, setWinnerId] = useState(null);
  const timers = useRef([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const later = useCallback((fn, ms) => {
    timers.current.push(setTimeout(fn, ms));
  }, []);

  const land = useCallback((pick) => {
    setSpinning(false);
    setRollingId(null);
    setReel((r) => ({ tag: "🎉 오늘은", name: pick.name, n: (r?.n ?? 0) + 1, win: true }));
    select(pick.id, true);
    setWinnerId(pick.id);
    later(() => setWinnerId(null), WIN_MS);
    later(() => setReel(null), REEL_MS);
  }, [select, later]);

  const spin = useCallback(() => {
    if (spinning) return;              // 연타는 무시. 돌던 게 끝나야 다음 판.
    const rows = candidates();
    if (!rows.length) return toast(emptyMsg);

    clearTimers();
    const pick = rows[Math.floor(Math.random() * rows.length)];

    // 후보가 둘뿐이면 룰렛이 연출이 아니라 지연일 뿐이다. 모션을 줄이는 설정도 마찬가지.
    // 결과는 릴로 그대로 보여준다 — 여기서만 토스트를 띄우면 알림이 두 종류가 된다.
    if (REDUCED_MOTION || rows.length < 3) return land(pick);

    setSpinning(true);
    select(null, false);   // 도는 동안 말풍선은 치운다. 지도 절반을 가린 채로는 마커가 안 보인다.

    // 지나갈 후보들. 같은 곳이 연달아 나오면 멈춘 것처럼 보이니 피한다.
    const seq = [];
    while (seq.length < SPIN_STEPS) {
      const s = rows[Math.floor(Math.random() * rows.length)];
      if (s !== seq[seq.length - 1]) seq.push(s);
    }
    seq.push(pick);

    let i = 0;
    const tick = () => {
      const s = seq[i++];
      setRollingId(s.id);
      setReel((r) => ({ tag: "오늘 뭐 먹지", name: s.name, n: (r?.n ?? 0) + 1 }));
      if (i < seq.length) {
        // 등속으로 돌면 그냥 깜빡이는 걸로 보인다. 감속이 있어야 '멈춘다'가 읽힌다.
        const t = i / seq.length;
        later(tick, 55 + 300 * t ** 3);
      } else {
        later(() => land(pick), 280);
      }
    };
    tick();
  }, [spinning, candidates, emptyMsg, toast, clearTimers, land, later, select]);

  return { spin, spinning, reel, rollingId, winnerId };
}
